import {useState} from 'react';
import {AxisBottom, AxisRight} from '@visx/axis';
import {curveMonotoneX} from '@visx/curve';
import {GlyphCircle} from '@visx/glyph';
import {Grid} from '@visx/grid';
import {Group} from '@visx/group';
import {ScaleSVG} from '@visx/responsive';
import {scaleLinear, scaleTime} from '@visx/scale';
import {LinePath} from '@visx/shape';
import {format, parseISO} from 'date-fns';

import {Tick} from '#component/partial/LifestyleTrendChart/components';
import {
  getDataDrivenXScaleDomain,
  getDataDrivenXScaleTicks,
} from '#component/partial/LifestyleTrendChart/LifestyleTrendChart.util';
import {useOnceEffect} from '#hook';
import {theme} from '#theme';

import {Glyph} from './component';
import {getAxisYTicks, getGridYTicks, getX, getY, getYScaleDomain} from './DeltaTrendChart.util';

import {DeltaTrendChartProps} from './DeltaTrendChart.types';

import * as css from './DeltaTrendChart.css';

const leftOffset = 40;
const topOffset = 20;

/**
 * DeltaTrendChart renders a chart depicting the user's delta age for all lab
 * submissions they've made over time. It uses a dynamic range of dates based
 * on the actual data range, with intelligently spaced tick marks that adapt
 * to the time span of the data (monthly, quarterly, semi-annually, or annually).
 *
 * The component is built using the visx charting library, which in turn uses
 * d3. For more information, review the docs for the relevant dependencies:
 *
 * - https://airbnb.io/visx/docs
 * - https://github.com/d3/d3/blob/main/API.md
 */
export const DeltaTrendChart = ({
  data = [],
  height = 246,
  width = 520,
  ...otherProps
}: DeltaTrendChartProps) => {
  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | null>(null);
  const currentDate = new Date();
  const isEmpty = data.length <= 0;
  const renderedChartWidth = width - leftOffset;
  const renderedChartHeight = height - topOffset * 3;
  const renderedXAxisOffset = height - topOffset * 1.5;
  const xTickValues = getDataDrivenXScaleTicks(data, currentDate);

  const xScale = scaleTime({
    domain: getDataDrivenXScaleDomain(data, currentDate),
    nice: true,
    range: [0, renderedChartWidth],
  });

  const yScale = scaleLinear({
    domain: getYScaleDomain(data),
    range: [renderedChartHeight, 0],
  });

  // The chart is animated into position on the History page, so defer showing
  // the default tooltip until that animation completes, otherwise the incorrect
  // position will be calculated.
  useOnceEffect(() => {
    const timeout = setTimeout(() => setActiveTooltipIndex(data ? data.length - 1 : null), 1000);

    return () => {
      clearTimeout(timeout);
    };
  });

  return (
    <ScaleSVG height={height} width={width} {...otherProps}>
      <Grid
        height={renderedChartHeight}
        numTicksColumns={0}
        rowTickValues={getGridYTicks(yScale)}
        stroke={theme.colors.moonMist}
        top={topOffset}
        width={renderedChartWidth}
        xScale={xScale}
        yScale={yScale}
      />

      <Grid
        height={renderedChartHeight}
        numTicksColumns={0}
        rowTickValues={[0]}
        stroke={theme.colors.dustyGray}
        top={topOffset}
        width={renderedChartWidth}
        xScale={xScale}
        yScale={yScale}
      />

      <Group top={topOffset}>
        <clipPath id="gradientBarsClipPath">
          <rect
            height={renderedChartHeight / 2 + topOffset}
            id="lineClipPath"
            width={renderedChartWidth}
            y={renderedChartHeight / 2}
          />
        </clipPath>

        <LinePath
          curve={curveMonotoneX}
          data={data}
          stroke={theme.colors.apricot}
          strokeWidth={3}
          x={d => xScale(getX(d)) ?? 0}
          y={d => yScale(getY(d)) ?? 0}
        />

        <LinePath
          // Clip "younger" portion of linepath — it only appears in the bottom
          // half of the chart (ie, below the `0` delta point on the Y axis.)
          clipPath="url(#gradientBarsClipPath)"
          curve={curveMonotoneX}
          data={data}
          stroke={theme.colors.burgundy}
          strokeWidth={3}
          x={d => xScale(getX(d)) ?? 0}
          y={d => yScale(getY(d)) ?? 0}
        />

        {data.map(({formattedDeltaAge, registeredDate, deltaAge}, index) => (
          <Glyph
            deltaAge={deltaAge}
            formattedDeltaAge={formattedDeltaAge}
            isTooltipOpen={index === activeTooltipIndex}
            onTooltipStateChange={isOpen => setActiveTooltipIndex(isOpen ? index : null)}
            isLast={index === data.length - 1}
            isYounger={deltaAge < 0}
            key={index}
            left={xScale(parseISO(registeredDate))}
            registeredDate={registeredDate}
            top={yScale(deltaAge)}
            xScale={xScale}
            yScale={yScale}
          />
        ))}

        {isEmpty && (
          <GlyphCircle
            className={css.ghostGlyph}
            left={xScale(currentDate)}
            size={100}
            top={yScale(0)}
          />
        )}
      </Group>

      <AxisBottom
        hideAxisLine
        hideTicks
        labelOffset={0}
        numTicks={4}
        scale={xScale}
        tickClassName={css.xTick}
        tickComponent={({formattedValue, ...otherProps}) => (
          <Tick
            formattedValue={formattedValue}
            isLast={formattedValue === format(new Date(xTickValues[0]), 'LLL ’yy')}
            {...otherProps}
          />
        )}
        tickFormat={date => format(date as Date, 'LLL ’yy')}
        tickValues={xTickValues}
        top={renderedXAxisOffset}
      />

      <AxisRight
        hideAxisLine
        hideTicks
        left={renderedChartWidth}
        scale={yScale}
        tickClassName={css.yTick}
        tickComponent={({formattedValue, dx, dy, x, y}) => (
          <text dx={dx} dy={dy} x={x} y={y}>
            {formattedValue}
          </text>
        )}
        tickFormat={delta =>
          delta === 0 ? '0' : `${delta > 0 ? '+' : ''}${(delta as number) / 12}y`
        }
        tickValues={getAxisYTicks(yScale)}
        top={topOffset}
      />
    </ScaleSVG>
  );
};
